import { jsx, jsxs } from "react/jsx-runtime";
import assert from "assert";
import { forwardRef, useRef, useReducer, useEffect } from "react";
import { flattenChildren } from "../../utils/flattenChildren.mjs";
import { Box } from "../Box/Box.mjs";
import { MenuItemDivider } from "../MenuItemDivider/MenuItemDivider.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { getNextIndex } from "../private/getNextIndex.mjs";
import { actionTypes } from "./MenuRenderer.actions.mjs";
import { MenuRendererContext } from "./MenuRendererContext.mjs";
import { MenuRendererItemContext } from "./MenuRendererItemContext.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { menuIsClosed, width, widthContent, placementBottom, stableScrollGutter, backdrop } from "./MenuRenderer.css.mjs";
import { ThemePortal } from "../ThemePortal/ThemePortal.mjs";
import { useOverlayPosition } from "@react-aria/overlays";
const {
  MENU_TRIGGER_UP,
  MENU_ITEM_UP,
  MENU_TRIGGER_DOWN,
  MENU_ITEM_DOWN,
  MENU_ITEM_ESCAPE,
  MENU_ITEM_TAB,
  MENU_ITEM_ENTER,
  MENU_ITEM_SPACE,
  MENU_ITEM_CLICK,
  MENU_ITEM_HOVER,
  MENU_ITEM_LEAVE,
  MENU_TRIGGER_ENTER,
  MENU_TRIGGER_SPACE,
  MENU_TRIGGER_CLICK,
  MENU_TRIGGER_TAB,
  MENU_TRIGGER_ESCAPE,
  BACKDROP_CLICK
} = actionTypes;
const CLOSED_INDEX = -1;
const CLOSE_REASON_EXIT = { reason: "exit" };
const initialState = {
  open: false,
  highlightIndex: CLOSED_INDEX,
  closeReason: CLOSE_REASON_EXIT
};
const MenuRenderer = ({
  onOpen,
  onClose,
  trigger,
  width: width2 = "content",
  align = "left",
  offsetSpace = "none",
  reserveIconSpace = false,
  placement = "bottom",
  shouldFlip = true,
  children,
  data,
  initialSelected,
  menuPosition = "absolute",
  ...restProps
}) => {
  var _a;
  const buttonRef = useRef(null);
  const lastOpen = useRef(false);
  const items = flattenChildren(children);
  const itemCount = items.filter((item) => !isDivider(item)).length;
  const currentIndex = initialSelected ?? CLOSED_INDEX;
  initialState.highlightIndex = currentIndex;
  assert(
    items.every(
      (item) => typeof item === "object" && "type" in item && // @ts-expect-error
      item.type.__isMenuItem__
    ),
    "All child nodes within a menu component must be a MenuItem, MenuItemLink, MenuItemCheckbox or MenuItemDivider: https://design-system.machobear.ca"
  );
  const [{ open, highlightIndex, closeReason }, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case MENU_TRIGGER_UP:
        case MENU_ITEM_UP: {
          return {
            ...state,
            open: true,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: getNextIndex(-1, state.highlightIndex, itemCount)
          };
        }
        case MENU_TRIGGER_DOWN:
        case MENU_ITEM_DOWN: {
          return {
            ...state,
            open: true,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: getNextIndex(1, state.highlightIndex, itemCount)
          };
        }
        case BACKDROP_CLICK:
        case MENU_TRIGGER_ESCAPE:
        case MENU_TRIGGER_TAB:
        case MENU_ITEM_ESCAPE:
        case MENU_ITEM_TAB: {
          return {
            ...state,
            open: false,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: currentIndex
          };
        }
        case MENU_ITEM_ENTER:
        case MENU_ITEM_SPACE:
        case MENU_ITEM_CLICK: {
          if ("formElement" in action && action.formElement) {
            return state;
          }
          return {
            ...state,
            open: false,
            closeReason: {
              reason: "selection",
              index: action.index,
              id: action.id
            },
            highlightIndex: currentIndex
          };
        }
        case MENU_ITEM_HOVER: {
          return { ...state, highlightIndex: action.value };
        }
        case MENU_ITEM_LEAVE: {
          return { ...state, highlightIndex: action.value };
        }
        case MENU_TRIGGER_ENTER:
        case MENU_TRIGGER_SPACE: {
          const nextOpen = !state.open;
          return {
            ...state,
            open: nextOpen,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: nextOpen ? 0 : CLOSED_INDEX
          };
        }
        case MENU_TRIGGER_CLICK: {
          const nextOpen = !state.open;
          return {
            ...state,
            open: nextOpen,
            closeReason: CLOSE_REASON_EXIT
          };
        }
        default:
          return state;
      }
    },
    initialState
  );
  useEffect(() => {
    if (lastOpen.current === open) {
      return;
    }
    if (open && typeof onOpen === "function") {
      onOpen();
    } else if (!open && typeof onClose === "function") {
      onClose(closeReason);
    }
    lastOpen.current = open;
  }, [onOpen, onClose, open, closeReason]);
  const focusTrigger = () => {
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  };
  const onTriggerKeyUp = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === " " && /firefox|iceweasel|fxios/i.test(navigator.userAgent)) {
      return;
    }
    const action = {
      ArrowDown: { type: MENU_TRIGGER_DOWN },
      ArrowUp: { type: MENU_TRIGGER_UP },
      Enter: { type: MENU_TRIGGER_ENTER },
      " ": { type: MENU_TRIGGER_SPACE },
      Escape: { type: MENU_TRIGGER_ESCAPE }
    };
    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
  };
  const onTriggerKeyDown = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Tab") {
      dispatch({ type: MENU_ITEM_TAB });
    }
    const isArrowPress = targetKey.indexOf("Arrow") === 0;
    const isActionKeyPress = targetKey === "Enter" || targetKey === " ";
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
  };
  const triggerProps = {
    "aria-haspopup": true,
    "aria-expanded": open,
    role: "button",
    tabIndex: 0,
    ref: buttonRef,
    onKeyUp: onTriggerKeyUp,
    onKeyDown: onTriggerKeyDown,
    onClick: (event) => {
      event.stopPropagation();
      event.preventDefault();
      dispatch({ type: MENU_TRIGGER_CLICK });
    }
  };
  const overlayRef = useRef(null);
  const ALIGN_MAP = {
    left: "start",
    right: "end"
  };
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: buttonRef,
    overlayRef,
    placement: `${placement} ${ALIGN_MAP[align]}`,
    offset: 4,
    isOpen: open,
    shouldFlip,
    maxHeight: 300
  });
  (_a = positionProps.style) == null ? true : delete _a.position;
  return /* @__PURE__ */ jsxs(Box, { ...buildDataAttributes({ data, validateRestProps: restProps }), children: [
    /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
      trigger(triggerProps, { open }),
      /* @__PURE__ */ jsx(ThemePortal, { children: /* @__PURE__ */ jsx(
        Menu,
        {
          ref: overlayRef,
          open,
          align,
          width: width2,
          placement,
          offsetSpace,
          highlightIndex,
          reserveIconSpace,
          focusTrigger,
          dispatch,
          position: menuPosition,
          ...positionProps,
          children: items
        }
      ) })
    ] }),
    open ? /* @__PURE__ */ jsx(
      Box,
      {
        onClick: (event) => {
          event.stopPropagation();
          event.preventDefault();
          dispatch({ type: BACKDROP_CLICK });
        },
        position: "fixed",
        zIndex: "dropdownBackdrop",
        top: 0,
        left: 0,
        className: backdrop
      }
    ) : null
  ] });
};
const isDivider = (node) => typeof node === "object" && node !== null && "type" in node && node.type === MenuItemDivider;
const borderRadius = "standard";
const Menu = forwardRef(
  ({
    width: width$1,
    placement,
    children,
    open,
    dispatch,
    focusTrigger,
    highlightIndex,
    reserveIconSpace,
    style,
    position
  }, ref) => {
    let dividerCount = 0;
    return /* @__PURE__ */ jsx(MenuRendererContext.Provider, { value: { reserveIconSpace }, children: /* @__PURE__ */ jsx(
      Box,
      {
        role: "menu",
        ref,
        overflow: "auto",
        boxShadow: "borderNeutralLight",
        borderRadius,
        background: "surface",
        transition: "fast",
        opacity: !open ? 0 : void 0,
        className: [
          !open && menuIsClosed,
          width$1 !== "content" && width[width$1],
          width$1 === "content" && widthContent,
          placement === "top" && placementBottom,
          (children == null ? void 0 : children.length) > 7 && stableScrollGutter
        ],
        style,
        position,
        children: /* @__PURE__ */ jsx(Box, { paddingY: "xxxsmall", children: children == null ? void 0 : children.map((item, i) => {
          if (isDivider(item)) {
            dividerCount++;
            return item;
          }
          const menuItemIndex = i - dividerCount;
          return /* @__PURE__ */ jsx(
            MenuRendererItemContext.Provider,
            {
              value: {
                isHighlighted: menuItemIndex === highlightIndex,
                index: menuItemIndex,
                dispatch,
                focusTrigger
              },
              children: item
            },
            menuItemIndex
          );
        }) })
      }
    ) });
  }
);
export {
  MenuRenderer
};
